import React from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";

import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import {
  ArrowLeft,
  Settings,
  Search,
  Filter,
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  ShieldCheck,
  CalendarDays,
  ClipboardCheck,
  Plus,
  Trash2,
  Info,
  Bell,
  Mail
} from "lucide-react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../components/ui/table";
import { Badge } from "../../components/ui/badge";
import { Switch } from "../../components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../../components/ui/dialog";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { api } from "../../utils/api";
import { toast } from "sonner";
import { useData } from "../../context/DataContext";
import { MultiSelect } from "../../components/ui/multi-select";
import { format } from "date-fns";

const AttendanceSettings = () => {
  const navigate = useNavigate();
  const { currentUser } = useAuth();

  // Tab 1: Access Authorization State
  const [loadingUsers, setLoadingUsers] = React.useState(true);
  const [users, setUsers] = React.useState<any[]>([]);
  const [totalCount, setTotalCount] = React.useState(0);
  const [totalPages, setTotalPages] = React.useState(0);
  const [currentPage, setCurrentPage] = React.useState(1);
  const [searchTerm, setSearchTerm] = React.useState("");

  // Tab: Handover Dialogue State
  const [loadingHandoverUsers, setLoadingHandoverUsers] = React.useState(true);
  const [handoverUsers, setHandoverUsers] = React.useState<any[]>([]);
  const [handoverSearch, setHandoverSearch] = React.useState("");
  const [handoverPage, setHandoverPage] = React.useState(1);
  const [totalHandoverPages, setTotalHandoverPages] = React.useState(0);
  const [totalHandoverCount, setTotalHandoverCount] = React.useState(0);
  const [savingHandoverUserId, setSavingHandoverUserId] = React.useState<number | null>(null);

  // Tab 2: Public Holidays State
  const [loadingHolidays, setLoadingHolidays] = React.useState(true);
  const [holidays, setHolidays] = React.useState<any[]>([]);
  const [isHolidayDialogOpen, setIsHolidayDialogOpen] = React.useState(false);
  const [newHoliday, setNewHoliday] = React.useState({
    name: "",
    startDate: "",
    endDate: "",
    doNotDeduct: true,
    recurYears: 1
  });

  const { departments } = useData();

  // Tab 4: Department Notifications State
  const [loadingDeptNotifications, setLoadingDeptNotifications] = React.useState(true);
  const [deptNotificationUsers, setDeptNotificationUsers] = React.useState<any[]>([]);
  const [deptNotificationSearch, setDeptNotificationSearch] = React.useState("");
  const [deptNotificationPage, setDeptNotificationPage] = React.useState(1);
  const [totalDeptNotificationPages, setTotalDeptNotificationPages] = React.useState(0);
  const [totalDeptNotificationCount, setTotalDeptNotificationCount] = React.useState(0);
  const [savingDeptNotifyUserId, setSavingDeptNotifyUserId] = React.useState<number | null>(null);

  // Build department options from useData (same source as rest of the app)
  const departmentOptions = React.useMemo(() => {
    return departments
      .filter((d: any) => !d.deleted && !d.archived && d.isActive !== false)
      .map((d: any) => ({ value: String(d.id), label: `${d.departmentCode} - ${d.departmentName}` }));
  }, [departments]);

  // Tab 3: Notifications State (Leave Request & Report Absentee)
  const [loadingApprovers, setLoadingApprovers] = React.useState(true);
  const [approvers, setApprovers] = React.useState<any[]>([]);
  const [approverSearch, setApproverSearch] = React.useState("");
  const [approverPage, setApproverPage] = React.useState(1);
  const [totalApproverPages, setTotalApproverPages] = React.useState(0);
  const [totalApproverCount, setTotalApproverCount] = React.useState(0);

  const fetchUsers = async (page: number, search: string) => {
    setLoadingUsers(true);
    try {
      const response = await api.get(`/api/attendance/settings/users?page=${page}&limit=10&search=${search}`);
      if (response.success) {
        setUsers(response.data.employees);
        setTotalCount(response.data.totalCount);
        setTotalPages(response.data.totalPages);
      }
    } catch (error) {
      console.error("Error fetching settings users:", error);
      toast.error("Failed to fetch users");
    } finally {
      setLoadingUsers(false);
    }
  };

  const fetchHandoverUsers = async (page: number, search: string) => {
    setLoadingHandoverUsers(true);
    try {
      const response = await api.get(`/api/attendance/settings/handover-dialogue-users?page=${page}&limit=10&search=${search}`);
      if (response.success) {
        setHandoverUsers(response.data.employees);
        setTotalHandoverCount(response.data.totalCount);
        setTotalHandoverPages(response.data.totalPages);
      }
    } catch (error) {
      console.error("Error fetching handover dialogue users:", error);
      toast.error("Failed to fetch users");
    } finally {
      setLoadingHandoverUsers(false);
    }
  };

  const handleHandoverToggle = async (userId: number, currentVal: boolean) => {
    setSavingHandoverUserId(userId);
    try {
      const response = await api.patch('/api/attendance/settings/handover-dialogue', {
        targetUserId: userId,
        enabled: !currentVal
      });

      if (response.success) {
        setHandoverUsers(handoverUsers.map(u => u.id === userId ? { ...u, handoverDialogue: !currentVal } : u));
        toast.success(response.message);
      } else {
        toast.error(response.message || "Failed to update setting");
      }
    } catch (error) {
      console.error("Error toggling handover dialogue:", error);
      toast.error("An error occurred while updating the setting");
    } finally {
      setSavingHandoverUserId(null);
    }
  };

  const fetchDeptNotificationUsers = async (page: number, search: string) => {
    setLoadingDeptNotifications(true);
    try {
      const response = await api.get(`/api/attendance/settings/department-notification-users?page=${page}&limit=15&search=${search}`);
      if (response.success) {
        setDeptNotificationUsers(response.data.users);
        setTotalDeptNotificationCount(response.data.totalCount);
        setTotalDeptNotificationPages(response.data.totalPages);
      }
    } catch (error) {
      console.error("Error fetching department notification users:", error);
      toast.error("Failed to fetch department notification users");
    } finally {
      setLoadingDeptNotifications(false);
    }
  };

  const handleDeptNotificationChange = async (userId: number, selectedDeptIds: string[]) => {
    setSavingDeptNotifyUserId(userId);
    try {
      const response = await api.patch('/api/attendance/settings/department-notifications', {
        targetUserId: userId,
        departmentIds: selectedDeptIds
      });

      if (response.success) {
        setDeptNotificationUsers(deptNotificationUsers.map(u =>
          u.id === userId ? { ...u, notifiedDepartmentIds: response.departmentIds } : u
        ));
        toast.success("Notification settings updated");
      } else {
        toast.error(response.message || "Failed to update notification settings");
      }
    } catch (error) {
      console.error("Error updating department notifications:", error);
      toast.error("An error occurred while updating settings");
    } finally {
      setSavingDeptNotifyUserId(null);
    }
  };

  const fetchHolidays = async () => {
    setLoadingHolidays(true);
    try {
      const response = await api.get('/api/attendance/public-holidays');
      if (response.success) {
        setHolidays(response.data);
      }
    } catch (error) {
      console.error("Error fetching holidays:", error);
      toast.error("Failed to fetch public holidays");
    } finally {
      setLoadingHolidays(false);
    }
  };

  const fetchApprovers = async (page: number, search: string) => {
    setLoadingApprovers(true);
    try {
      const response = await api.get(`/api/attendance/settings/approver-list?page=${page}&limit=15&search=${search}`);
      if (response.success) {
        setApprovers(response.data.users);
        setTotalApproverCount(response.data.totalCount);
        setTotalApproverPages(response.data.totalPages);
      }
    } catch (error) {
      console.error("Error fetching approver list:", error);
      toast.error("Failed to fetch approvers list");
    } finally {
      setLoadingApprovers(false);
    }
  };

  React.useEffect(() => {
    const timer = setTimeout(() => {
      fetchDeptNotificationUsers(deptNotificationPage, deptNotificationSearch);
    }, 300);
    return () => clearTimeout(timer);
  }, [deptNotificationPage, deptNotificationSearch]);

  React.useEffect(() => {
    const timer = setTimeout(() => {
      fetchUsers(currentPage, searchTerm);
    }, 300);
    return () => clearTimeout(timer);
  }, [currentPage, searchTerm]);

  React.useEffect(() => {
    const timer = setTimeout(() => {
      fetchHandoverUsers(handoverPage, handoverSearch);
    }, 300);
    return () => clearTimeout(timer);
  }, [handoverPage, handoverSearch]);

  React.useEffect(() => {
    fetchHolidays();
  }, []);

  React.useEffect(() => {
    const timer = setTimeout(() => {
      fetchApprovers(approverPage, approverSearch);
    }, 300);
    return () => clearTimeout(timer);
  }, [approverPage, approverSearch]);

  const handleToggle = async (userId: number, currentVal: boolean) => {
    try {
      const newVal = !currentVal;
      const response = await api.patch('/api/attendance/settings/toggle-visibility', {
        targetUserId: userId,
        showInAttendance: newVal
      });

      if (response.success) {
        setUsers(users.map(u => u.id === userId ? { ...u, showInAttendance: newVal } : u));
        toast.success(response.message);
      } else {
        toast.error(response.message || "Failed to update setting");
      }
    } catch (error) {
      console.error("Error toggling visibility:", error);
      toast.error("An error occurred while updating the setting");
    }
  };

  const handleToggleCC = async (userId: number) => {
    try {
      const response = await api.patch('/api/attendance/settings/toggle-response-cc', {
        targetUserId: userId
      });

      if (response.success) {
        setApprovers(approvers.map(u => u.id === userId ? { ...u, isLeaveResponseCCd: response.isCCd } : u));
        toast.success(response.message);
      } else {
        toast.error(response.message || "Failed to update notification settings");
      }
    } catch (error) {
      console.error("Error toggling notification CC:", error);
      toast.error("An error occurred while updating settings");
    }
  };

  const handleToggleAbsenceReport = async (userId: number) => {
    try {
      const response = await api.patch('/api/attendance/settings/toggle-absence-report-cc', {
        targetUserId: userId
      });

      if (response.success) {
        setApprovers(approvers.map(u => u.id === userId ? { ...u, isAbsenteeReportCCd: response.isAbsenteeReportCCd } : u));
        toast.success(response.message);
      } else {
        toast.error(response.message || "Failed to update absence report settings");
      }
    } catch (error) {
      console.error("Error toggling absence report CC:", error);
      toast.error("An error occurred while updating absence report settings");
    }
  };

  const handleAddHoliday = async () => {
    if (!newHoliday.name || !newHoliday.startDate) {
      toast.error("Please provide a name and start date");
      return;
    }

    try {
      const response = await api.post('/api/attendance/public-holidays', newHoliday);
      if (response.success) {
        toast.success("Public holiday added");
        fetchHolidays();
        setIsHolidayDialogOpen(false);
        setNewHoliday({ name: "", startDate: "", endDate: "", doNotDeduct: true, recurYears: 1 });
      }
    } catch (error) {
      console.error("Error adding holiday:", error);
      toast.error("Failed to add public holiday");
    }
  };

  const handleDeleteHoliday = async (id: number) => {
    try {
      const response = await api.delete(`/api/attendance/public-holidays/${id}`);
      if (response.success) {
        toast.success("Public holiday deleted");
        setHolidays(holidays.filter(h => h.id !== id));
      }
    } catch (error) {
      console.error("Error deleting holiday:", error);
      toast.error("Failed to delete public holiday");
    }
  };

  return (
    <div className="p-6 space-y-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate('/attendance')}
            className="rounded-full hover:bg-gray-100"
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Attendance Settings</h1>
            <p className="text-sm text-gray-500">Manage global attendance policies and configurations</p>
          </div>
        </div>
      </div>

      <Tabs defaultValue="access" className="w-full">
        <TabsList className="grid w-full grid-cols-4 max-w-3xl mb-8">
          <TabsTrigger value="access" className="flex items-center gap-2">
            <ShieldCheck className="h-4 w-4" />
            Module Access
          </TabsTrigger>
          <TabsTrigger value="handover" className="flex items-center gap-2">
            <ClipboardCheck className="h-4 w-4" />
            Handover Dialogue
          </TabsTrigger>
          <TabsTrigger value="notifications" className="flex items-center gap-2">
            <Bell className="h-4 w-4" />
            Notifications
          </TabsTrigger>
          <TabsTrigger value="holidays" className="flex items-center gap-2">
            <CalendarDays className="h-4 w-4" />
            Public Holidays
          </TabsTrigger>
        </TabsList>

        <TabsContent value="access">
          <Card className="border-gray-200 shadow-sm overflow-hidden text-sm lg:text-base">
            <CardHeader className="bg-white border-b border-gray-100">
              <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div>
                  <CardTitle className="text-lg font-semibold flex items-center gap-2">
                    <ShieldCheck className="h-5 w-5 text-gray-400" />
                    Access Authorization
                  </CardTitle>
                  <CardDescription>
                    Enable or disable the leave module for specific users (e.g. casual workers).
                  </CardDescription>
                </div>
                <div className="relative w-full md:w-80">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Search by name..."
                    value={searchTerm}
                    onChange={(e) => {
                      setSearchTerm(e.target.value);
                      setCurrentPage(1);
                    }}
                    className="w-full pl-10 pr-4 py-2 bg-gray-50 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm transition-all"
                  />
                </div>
              </div>
            </CardHeader>
            <CardContent className="p-0">
              {/* Mobile Card View */}
              <div className="block md:hidden divide-y divide-gray-100">
                {loadingUsers ? (
                  <div className="text-center py-20 text-gray-500">
                    <div className="flex flex-col items-center gap-2">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                      <span>Loading users...</span>
                    </div>
                  </div>
                ) : users.length === 0 ? (
                  <div className="text-center py-20 text-gray-500">
                    <div className="flex flex-col items-center gap-2 px-6">
                      <Filter className="h-10 w-10 text-gray-200" />
                      <p className="font-medium">No users found</p>
                      <p className="text-xs">Try adjusting your search terms</p>
                    </div>
                  </div>
                ) : (
                  users.map((user) => (
                    <div key={user.id} className="p-4 space-y-4 bg-white hover:bg-gray-50/50 transition-colors">
                      <div className="flex justify-between items-start">
                        <div className="min-w-0 flex-1">
                          <h3 className="font-bold text-gray-900 truncate text-base">{user.name}</h3>
                          <p className="text-xs text-gray-500 mt-0.5 truncate">
                            {user.jobRole} • {user.department}
                          </p>
                        </div>
                        <Badge variant="outline" className="text-[10px] px-1.5 h-5 flex-shrink-0 ml-2 border-gray-200 font-semibold">
                          {user.userRole}
                        </Badge>
                      </div>
                      <div className="flex items-center justify-between bg-gray-50/80 p-3 rounded-xl border border-gray-100 group transition-all active:scale-[0.98]">
                        <span className="text-xs font-semibold text-gray-600">Visibility</span>
                        <div className="flex items-center gap-3">
                          <span className={`text-[10px] font-bold uppercase tracking-wider ${user.showInAttendance ? 'text-green-600' : 'text-gray-400'}`}>
                            {user.showInAttendance ? 'Shown' : 'Hidden'}
                          </span>
                          <Switch
                            checked={user.showInAttendance}
                            onCheckedChange={() => handleToggle(user.id, user.showInAttendance)}
                            disabled={user.id == currentUser?.id}
                          />
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>

              {/* Desktop Table View */}
              <div className="hidden md:block">
                <Table>
                  <TableHeader className="bg-gray-50/50">
                    <TableRow>
                      <TableHead className="font-semibold text-gray-600 px-6 py-4">Name</TableHead>
                      <TableHead className="font-semibold text-gray-600 px-6 py-4">Department</TableHead>
                      <TableHead className="font-semibold text-gray-600 px-6 py-4">Job Role</TableHead>
                      <TableHead className="font-semibold text-gray-600 px-6 py-4">User Role</TableHead>
                      <TableHead className="font-semibold text-gray-600 px-6 py-4 text-center">Module Access</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {loadingUsers ? (
                      <TableRow>
                        <TableCell colSpan={5} className="text-center py-20 text-gray-500">
                          <div className="flex flex-col items-center gap-2">
                            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                            <span>Loading users...</span>
                          </div>
                        </TableCell>
                      </TableRow>
                    ) : users.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={5} className="text-center py-20 text-gray-500">
                          <div className="flex flex-col items-center gap-2">
                            <Filter className="h-10 w-10 text-gray-200" />
                            <p className="font-medium">No users found</p>
                            <p className="text-sm">Try adjusting your search terms</p>
                          </div>
                        </TableCell>
                      </TableRow>
                    ) : (
                      users.map((user) => (
                        <TableRow key={user.id} className="hover:bg-gray-50/50 transition-colors">
                          <TableCell className="px-6 py-4 font-medium text-gray-900 text-sm whitespace-nowrap">{user.name} {user.id === currentUser?.id ? <span className="text-xs text-gray-500">(You)</span> : ''}</TableCell>
                          <TableCell className="px-6 py-4 text-gray-600 text-sm whitespace-nowrap">{user.department}</TableCell>
                          <TableCell className="px-6 py-4 text-gray-600 text-sm whitespace-nowrap">{user.jobRole}</TableCell>
                          <TableCell className="px-6 py-4">
                            <Badge variant="outline" className="font-normal border-gray-200 text-xs">
                              {user.userRole}
                            </Badge>
                          </TableCell>
                          <TableCell className="px-6 py-4">
                            <div className="flex items-center justify-center gap-3">
                              <span className={`text-xs font-medium ${user.showInAttendance ? 'text-green-600' : 'text-gray-400'}`}>
                                {user.showInAttendance ? 'Shown' : 'Hidden'}
                              </span>
                              <Switch
                                checked={user.showInAttendance}
                                onCheckedChange={() => handleToggle(user.id, user.showInAttendance)}
                                disabled={user.id === currentUser?.id}
                              />
                            </div>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="px-4 py-4 md:px-6 border-t border-gray-100 flex flex-col md:flex-row items-center justify-between gap-4 bg-white">
                  <p className="text-sm text-gray-500 order-2 md:order-1">
                    Showing <span className="font-medium">{((currentPage - 1) * 10) + 1}</span> to <span className="font-medium">{Math.min(currentPage * 10, totalCount)}</span> of <span className="font-medium">{totalCount}</span> users
                  </p>
                  <div className="flex items-center gap-2 order-1 md:order-2">
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={currentPage === 1 || loadingUsers}
                      onClick={() => setCurrentPage(prev => prev - 1)}
                      className="h-9 w-9 md:w-auto md:px-3 p-0"
                    >
                      <ChevronLeft className="h-4 w-4 md:mr-1" />
                      <span className="hidden md:inline">Previous</span>
                    </Button>
                    <div className="flex items-center gap-1 font-medium text-sm px-2 bg-gray-50 rounded-lg h-9">
                      <span className="text-gray-400 hidden sm:inline">Page</span>
                      <span className="text-blue-600 px-1">{currentPage}</span>
                      <span className="text-gray-300">/</span>
                      <span className="px-1">{totalPages}</span>
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={currentPage === totalPages || loadingUsers}
                      onClick={() => setCurrentPage(prev => prev + 1)}
                      className="h-9 w-9 md:w-auto md:px-3 p-0"
                    >
                      <span className="hidden md:inline">Next</span>
                      <ChevronRight className="h-4 w-4 md:ml-1" />
                    </Button>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Info Alert */}
          <div className="mt-8 bg-blue-50 border border-blue-100 rounded-xl p-4 flex gap-3">
            <div className="p-1 h-fit bg-blue-100 rounded-lg">
              <CheckCircle2 className="h-4 w-4 text-blue-600" />
            </div>
            <div className="text-sm">
              <p className="font-semibold text-blue-900">About module access</p>
              <p className="text-blue-700/80 mt-0.5">
                Disabling access prevents the user from viewing or using the Attendance & Leave module.
                This is typical for casual workers or contractors without leave entitlements.
                All historical data is preserved.
              </p>
            </div>
          </div>
        </TabsContent>

        <TabsContent value="handover">
          <Card className="border-gray-200 shadow-sm overflow-hidden text-sm lg:text-base">
            <CardHeader className="bg-white border-b border-gray-100">
              <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div>
                  <CardTitle className="text-lg font-semibold flex items-center gap-2">
                    <ClipboardCheck className="h-5 w-5 text-gray-400" />
                    Handover Dialogue
                  </CardTitle>
                  <CardDescription>
                    Choose who must confirm a handover before an annual leave request is submitted.
                  </CardDescription>
                </div>
                <div className="relative w-full md:w-80">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Search by name..."
                    value={handoverSearch}
                    onChange={(e) => {
                      setHandoverSearch(e.target.value);
                      setHandoverPage(1);
                    }}
                    className="w-full pl-10 pr-4 py-2 bg-gray-50 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm transition-all"
                  />
                </div>
              </div>
            </CardHeader>
            <CardContent className="p-0">
              {/* Mobile Card View */}
              <div className="block md:hidden divide-y divide-gray-100">
                {loadingHandoverUsers ? (
                  <div className="text-center py-20 text-gray-500">
                    <div className="flex flex-col items-center gap-2">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                      <span>Loading users...</span>
                    </div>
                  </div>
                ) : handoverUsers.length === 0 ? (
                  <div className="text-center py-20 text-gray-500">
                    <div className="flex flex-col items-center gap-2 px-6">
                      <Filter className="h-10 w-10 text-gray-200" />
                      <p className="font-medium">No users found</p>
                      <p className="text-xs">Try adjusting your search terms</p>
                    </div>
                  </div>
                ) : (
                  handoverUsers.map((user) => (
                    <div key={user.id} className="p-4 space-y-4 bg-white hover:bg-gray-50/50 transition-colors">
                      <div className="flex justify-between items-start">
                        <div className="min-w-0 flex-1">
                          <h3 className="font-bold text-gray-900 truncate text-base">{user.name}</h3>
                          <p className="text-xs text-gray-500 mt-0.5 truncate">
                            {user.jobRole} • {user.department}
                          </p>
                        </div>
                        <Badge variant="outline" className="text-[10px] px-1.5 h-5 flex-shrink-0 ml-2 border-gray-200 font-semibold">
                          {user.userRole}
                        </Badge>
                      </div>
                      <div className="flex items-center justify-between bg-gray-50/80 p-3 rounded-xl border border-gray-100 transition-all">
                        <span className="text-xs font-semibold text-gray-600">Handover dialogue</span>
                        <div className="flex items-center gap-3">
                          <span className={`text-[10px] font-bold uppercase tracking-wider ${user.handoverDialogue ? 'text-green-600' : 'text-gray-400'}`}>
                            {user.handoverDialogue ? 'On' : 'Off'}
                          </span>
                          <Switch
                            checked={user.handoverDialogue}
                            onCheckedChange={() => handleHandoverToggle(user.id, user.handoverDialogue)}
                            disabled={user.id === currentUser?.id || savingHandoverUserId === user.id}
                          />
                        </div>
                      </div>
                    </div>
                  ))
                )}
              </div>

              {/* Desktop Table View */}
              <div className="hidden md:block">
                <Table>
                  <TableHeader className="bg-gray-50/50">
                    <TableRow>
                      <TableHead className="font-semibold text-gray-600 px-6 py-4">Name</TableHead>
                      <TableHead className="font-semibold text-gray-600 px-6 py-4">Department</TableHead>
                      <TableHead className="font-semibold text-gray-600 px-6 py-4">Job Role</TableHead>
                      <TableHead className="font-semibold text-gray-600 px-6 py-4">User Role</TableHead>
                      <TableHead className="font-semibold text-gray-600 px-6 py-4 text-center">Handover Dialogue</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {loadingHandoverUsers ? (
                      <TableRow>
                        <TableCell colSpan={5} className="text-center py-20 text-gray-500">
                          <div className="flex flex-col items-center gap-2">
                            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                            <span>Loading users...</span>
                          </div>
                        </TableCell>
                      </TableRow>
                    ) : handoverUsers.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={5} className="text-center py-20 text-gray-500">
                          <div className="flex flex-col items-center gap-2">
                            <Filter className="h-10 w-10 text-gray-200" />
                            <p className="font-medium">No users found</p>
                            <p className="text-sm">Try adjusting your search terms</p>
                          </div>
                        </TableCell>
                      </TableRow>
                    ) : (
                      handoverUsers.map((user) => (
                        <TableRow key={user.id} className="hover:bg-gray-50/50 transition-colors">
                          <TableCell className="px-6 py-4 font-medium text-gray-900 text-sm whitespace-nowrap">
                            {user.name} {user.id === currentUser?.id ? <span className="text-xs text-gray-500">(You)</span> : ''}
                          </TableCell>
                          <TableCell className="px-6 py-4 text-gray-600 text-sm whitespace-nowrap">{user.department}</TableCell>
                          <TableCell className="px-6 py-4 text-gray-600 text-sm whitespace-nowrap">{user.jobRole}</TableCell>
                          <TableCell className="px-6 py-4">
                            <Badge variant="outline" className="font-normal border-gray-200 text-xs">
                              {user.userRole}
                            </Badge>
                          </TableCell>
                          <TableCell className="px-6 py-4">
                            <div className="flex items-center justify-center gap-3">
                              <span className={`text-xs font-medium ${user.handoverDialogue ? 'text-green-600' : 'text-gray-400'}`}>
                                {user.handoverDialogue ? 'On' : 'Off'}
                              </span>
                              <Switch
                                checked={user.handoverDialogue}
                                onCheckedChange={() => handleHandoverToggle(user.id, user.handoverDialogue)}
                                disabled={user.id === currentUser?.id || savingHandoverUserId === user.id}
                              />
                            </div>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </div>

              {/* Pagination */}
              {totalHandoverPages > 1 && (
                <div className="px-4 py-4 md:px-6 border-t border-gray-100 flex flex-col md:flex-row items-center justify-between gap-4 bg-white">
                  <p className="text-sm text-gray-500 order-2 md:order-1">
                    Showing <span className="font-medium">{((handoverPage - 1) * 10) + 1}</span> to <span className="font-medium">{Math.min(handoverPage * 10, totalHandoverCount)}</span> of <span className="font-medium">{totalHandoverCount}</span> users
                  </p>
                  <div className="flex items-center gap-2 order-1 md:order-2">
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={handoverPage === 1 || loadingHandoverUsers}
                      onClick={() => setHandoverPage(prev => prev - 1)}
                      className="h-9 w-9 md:w-auto md:px-3 p-0"
                    >
                      <ChevronLeft className="h-4 w-4 md:mr-1" />
                      <span className="hidden md:inline">Previous</span>
                    </Button>
                    <div className="flex items-center gap-1 font-medium text-sm px-2 bg-gray-50 rounded-lg h-9">
                      <span className="text-gray-400 hidden sm:inline">Page</span>
                      <span className="text-blue-600 px-1">{handoverPage}</span>
                      <span className="text-gray-300">/</span>
                      <span className="px-1">{totalHandoverPages}</span>
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={handoverPage === totalHandoverPages || loadingHandoverUsers}
                      onClick={() => setHandoverPage(prev => prev + 1)}
                      className="h-9 w-9 md:w-auto md:px-3 p-0"
                    >
                      <span className="hidden md:inline">Next</span>
                      <ChevronRight className="h-4 w-4 md:ml-1" />
                    </Button>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Info Alert */}
          <div className="mt-8 bg-blue-50 border border-blue-100 rounded-xl p-4 flex gap-3">
            <div className="p-1 h-fit bg-blue-100 rounded-lg">
              <CheckCircle2 className="h-4 w-4 text-blue-600" />
            </div>
            <div className="text-sm">
              <p className="font-semibold text-blue-900">About the handover dialogue</p>
              <p className="text-blue-700/80 mt-0.5">
                The dialogue is off by default. When switched on, an annual leave request from that
                user must nominate a cover and confirm the handover checklist before it can be
                submitted — the answers are shown to the approver and kept against the request for
                HR. Personal, sick and unpaid leave are never prompted.
              </p>
            </div>
          </div>
        </TabsContent>

        <TabsContent value="notifications">
          <Tabs defaultValue="leave-request" className="w-full">
            <TabsList className="grid w-full grid-cols-3 max-w-lg mb-6">
              <TabsTrigger value="leave-request" className="flex items-center gap-2">
                <Mail className="h-4 w-4" />
                Leave Request
              </TabsTrigger>
              <TabsTrigger value="report-absentee" className="flex items-center gap-2">
                <Bell className="h-4 w-4" />
                Report Absentee
              </TabsTrigger>
              <TabsTrigger value="department-notifications" className="flex items-center gap-2">
                <Filter className="h-4 w-4" />
                Department
              </TabsTrigger>
            </TabsList>

            <TabsContent value="leave-request">
              <Card className="border-gray-200 shadow-sm overflow-hidden text-sm lg:text-base">
                <CardHeader className="bg-white border-b border-gray-100">
                  <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                    <div>
                      <CardTitle className="text-lg font-semibold flex items-center gap-2">
                        <Mail className="h-5 w-5 text-gray-400" />
                        Leave Response Notifications
                      </CardTitle>
                      <CardDescription>
                        Configure users who should be CC'd on all leave request approval and rejection emails.
                      </CardDescription>
                    </div>
                    <div className="relative w-full md:w-80">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                      <input
                        type="text"
                        placeholder="Search managers..."
                        value={approverSearch}
                        onChange={(e) => {
                          setApproverSearch(e.target.value);
                          setApproverPage(1);
                        }}
                        className="w-full pl-10 pr-4 py-2 bg-gray-50 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm transition-all"
                      />
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="p-0">
                  <div className="hidden md:block">
                    <Table>
                      <TableHeader className="bg-gray-50/50">
                        <TableRow>
                          <TableHead className="font-semibold text-gray-600 px-6 py-4">Name</TableHead>
                          <TableHead className="font-semibold text-gray-600 px-6 py-4">Department</TableHead>
                          <TableHead className="font-semibold text-gray-600 px-6 py-4">User Role</TableHead>
                          <TableHead className="font-semibold text-gray-600 px-6 py-4 text-center">Receive Response CC</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {loadingApprovers ? (
                          <TableRow>
                            <TableCell colSpan={4} className="text-center py-20 text-gray-500">
                              <div className="flex flex-col items-center gap-2">
                                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                                <span>Loading approvers...</span>
                              </div>
                            </TableCell>
                          </TableRow>
                        ) : approvers.length === 0 ? (
                          <TableRow>
                            <TableCell colSpan={4} className="text-center py-20 text-gray-500">
                              <div className="flex flex-col items-center gap-2">
                                <Filter className="h-10 w-10 text-gray-200" />
                                <p className="font-medium">No managers found</p>
                              </div>
                            </TableCell>
                          </TableRow>
                        ) : (
                          approvers.map((user) => (
                            <TableRow key={user.id} className="hover:bg-gray-50/50 transition-colors">
                              <TableCell className="px-6 py-4 font-medium text-gray-900 text-sm whitespace-nowrap">{user.name}</TableCell>
                              <TableCell className="px-6 py-4 text-gray-600 text-sm whitespace-nowrap">{user.department}</TableCell>
                              <TableCell className="px-6 py-4">
                                <Badge variant="outline" className="font-normal border-gray-200 text-xs text-gray-500">
                                  {user.userRole}
                                </Badge>
                              </TableCell>
                              <TableCell className="px-6 py-4">
                                <div className="flex items-center justify-center gap-3">
                                  <span className={`text-xs font-medium ${user.isLeaveResponseCCd ? 'text-blue-600' : 'text-gray-400'}`}>
                                    {user.isLeaveResponseCCd ? 'Subscribed' : 'Off'}
                                  </span>
                                  <Switch
                                    checked={user.isLeaveResponseCCd}
                                    onCheckedChange={() => handleToggleCC(user.id)}
                                  />
                                </div>
                              </TableCell>
                            </TableRow>
                          ))
                        )}
                      </TableBody>
                    </Table>
                  </div>

                  {/* Mobile View for Leave Response */}
                  <div className="block md:hidden divide-y divide-gray-100">
                    {loadingApprovers ? (
                      <div className="text-center py-20">Loading...</div>
                    ) : approvers.map((user) => (
                      <div key={user.id} className="p-4 flex items-center justify-between bg-white hover:bg-gray-50/50 transition-colors">
                        <div>
                          <h3 className="font-bold text-gray-900 text-sm">{user.name}</h3>
                          <p className="text-xs text-gray-500">{user.userRole} • {user.department}</p>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className={`text-[10px] font-bold uppercase tracking-wider ${user.isLeaveResponseCCd ? 'text-blue-600' : 'text-gray-400'}`}>
                            {user.isLeaveResponseCCd ? 'Subscribed' : 'Off'}
                          </span>
                          <Switch
                            checked={user.isLeaveResponseCCd}
                            onCheckedChange={() => handleToggleCC(user.id)}
                          />
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Pagination */}
                  {totalApproverPages > 1 && (
                    <div className="px-4 py-4 md:px-6 border-t border-gray-100 flex flex-col md:flex-row items-center justify-between gap-4 bg-white">
                      <p className="text-sm text-gray-500">
                        Showing <span className="font-medium">{((approverPage - 1) * 15) + 1}</span> to <span className="font-medium">{Math.min(approverPage * 15, totalApproverCount)}</span> of <span className="font-medium">{totalApproverCount}</span> approvers
                      </p>
                      <div className="flex items-center gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          disabled={approverPage === 1 || loadingApprovers}
                          onClick={() => setApproverPage(prev => prev - 1)}
                        >
                          <ChevronLeft className="h-4 w-4 mr-1" />
                          Previous
                        </Button>
                        <div className="flex items-center gap-1 font-medium text-sm px-2 bg-gray-50 rounded-lg h-9">
                          <span className="text-blue-600 font-bold">{approverPage}</span>
                          <span className="text-gray-300">/</span>
                          <span>{totalApproverPages}</span>
                        </div>
                        <Button
                          variant="outline"
                          size="sm"
                          disabled={approverPage === totalApproverPages || loadingApprovers}
                          onClick={() => setApproverPage(prev => prev + 1)}
                        >
                          Next
                          <ChevronRight className="h-4 w-4 ml-1" />
                        </Button>
                      </div>
                    </div>
                  )}

                  {/* Info Alert */}
                  <div className="m-6 bg-blue-50 border border-blue-100 rounded-xl p-4 flex gap-3">
                    <div className="p-1 h-fit bg-blue-100 rounded-lg">
                      <Mail className="h-4 w-4 text-blue-600" />
                    </div>
                    <div className="text-sm">
                      <p className="font-semibold text-blue-900">About Leave Response Notifications</p>
                      <p className="text-blue-700/80 mt-0.5">
                        Users toggled "On" will receive a CC email whenever any leave request is Approved or Rejected in the system.
                        This is useful for payroll managers or HR administrators who need to track leave status changes in real-time.
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="department-notifications">
              <Card className="border-gray-200 shadow-sm overflow-hidden text-sm lg:text-base">
                <CardHeader className="bg-white border-b border-gray-100">
                  <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                    <div>
                      <CardTitle className="text-lg font-semibold flex items-center gap-2">
                        <Filter className="h-5 w-5 text-gray-400" />
                        Department Notifications
                      </CardTitle>
                      <CardDescription>
                        Select which departments each employee should receive notifications for once a request is approved or a report absence is made
                      </CardDescription>
                    </div>
                    <div className="relative w-full md:w-80">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                      <input
                        type="text"
                        placeholder="Search employees..."
                        value={deptNotificationSearch}
                        onChange={(e) => {
                          setDeptNotificationSearch(e.target.value);
                          setDeptNotificationPage(1);
                        }}
                        className="w-full pl-10 pr-4 py-2 bg-gray-50 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm transition-all"
                      />
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="p-0">
                  <div className="hidden md:block">
                    <Table>
                      <TableHeader className="bg-gray-50/50">
                        <TableRow>
                          <TableHead className="font-semibold text-gray-600 px-6 py-4">Name</TableHead>
                          <TableHead className="font-semibold text-gray-600 px-6 py-4">Department</TableHead>
                          <TableHead className="font-semibold text-gray-600 px-6 py-4">User Role</TableHead>
                          <TableHead className="font-semibold text-gray-600 px-6 py-4">Notify For Departments</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {loadingDeptNotifications ? (
                          <TableRow>
                            <TableCell colSpan={4} className="text-center py-20 text-gray-500">
                              <div className="flex flex-col items-center gap-2">
                                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                                <span>Loading employees...</span>
                              </div>
                            </TableCell>
                          </TableRow>
                        ) : deptNotificationUsers.length === 0 ? (
                          <TableRow>
                            <TableCell colSpan={4} className="text-center py-20 text-gray-500">
                              <div className="flex flex-col items-center gap-2">
                                <Filter className="h-10 w-10 text-gray-200" />
                                <p className="font-medium">No employees found</p>
                              </div>
                            </TableCell>
                          </TableRow>
                        ) : (
                          deptNotificationUsers.map((user) => (
                            <TableRow key={user.id} className="hover:bg-gray-50/50 transition-colors">
                              <TableCell className="px-6 py-4 font-medium text-gray-900 text-sm whitespace-nowrap">{user.name}</TableCell>
                              <TableCell className="px-6 py-4 text-gray-600 text-sm whitespace-nowrap">{user.department}</TableCell>
                              <TableCell className="px-6 py-4">
                                <Badge variant="outline" className="font-normal border-gray-200 text-xs text-gray-500">
                                  {user.userRole}
                                </Badge>
                              </TableCell>
                              <TableCell className="px-6 py-4">
                                <div className="min-w-[200px]">
                                  {savingDeptNotifyUserId === user.id ? (
                                    <div className="flex items-center gap-2 text-sm text-gray-500">
                                      <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
                                      Saving...
                                    </div>
                                  ) : (
                                    <MultiSelect
                                      options={departmentOptions}
                                      selected={user.notifiedDepartmentIds || []}
                                      onChange={(selected) => handleDeptNotificationChange(user.id, selected)}
                                      placeholder="Select departments..."
                                      emptyMessage="No departments found."
                                    />
                                  )}
                                </div>
                              </TableCell>
                            </TableRow>
                          ))
                        )}
                      </TableBody>
                    </Table>
                  </div>

                  {/* Mobile View for Department Notifications */}
                  <div className="block md:hidden divide-y divide-gray-100">
                    {loadingDeptNotifications ? (
                      <div className="text-center py-20">Loading...</div>
                    ) : deptNotificationUsers.map((user) => (
                      <div key={user.id} className="p-4 space-y-3 bg-white hover:bg-gray-50/50 transition-colors">
                        <div className="flex justify-between items-start">
                          <div>
                            <h3 className="font-bold text-gray-900 text-sm">{user.name}</h3>
                            <p className="text-xs text-gray-500">{user.userRole} • {user.department}</p>
                          </div>
                        </div>
                        <div>
                          <span className="text-xs font-semibold text-gray-600 block mb-1.5">Notify For Departments</span>
                          {savingDeptNotifyUserId === user.id ? (
                            <div className="flex items-center gap-2 text-sm text-gray-500">
                              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
                              Saving...
                            </div>
                          ) : (
                            <MultiSelect
                              options={departmentOptions}
                              selected={user.notifiedDepartmentIds || []}
                              onChange={(selected) => handleDeptNotificationChange(user.id, selected)}
                              placeholder="Select departments..."
                              emptyMessage="No departments found."
                            />
                          )}
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Pagination */}
                  {totalDeptNotificationPages > 1 && (
                    <div className="px-4 py-4 md:px-6 border-t border-gray-100 flex flex-col md:flex-row items-center justify-between gap-4 bg-white">
                      <p className="text-sm text-gray-500">
                        Showing <span className="font-medium">{((deptNotificationPage - 1) * 15) + 1}</span> to <span className="font-medium">{Math.min(deptNotificationPage * 15, totalDeptNotificationCount)}</span> of <span className="font-medium">{totalDeptNotificationCount}</span> employees
                      </p>
                      <div className="flex items-center gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          disabled={deptNotificationPage === 1 || loadingDeptNotifications}
                          onClick={() => setDeptNotificationPage(prev => prev - 1)}
                        >
                          <ChevronLeft className="h-4 w-4 mr-1" />
                          Previous
                        </Button>
                        <div className="flex items-center gap-1 font-medium text-sm px-2 bg-gray-50 rounded-lg h-9">
                          <span className="text-blue-600 font-bold">{deptNotificationPage}</span>
                          <span className="text-gray-300">/</span>
                          <span>{totalDeptNotificationPages}</span>
                        </div>
                        <Button
                          variant="outline"
                          size="sm"
                          disabled={deptNotificationPage === totalDeptNotificationPages || loadingDeptNotifications}
                          onClick={() => setDeptNotificationPage(prev => prev + 1)}
                        >
                          Next
                          <ChevronRight className="h-4 w-4 ml-1" />
                        </Button>
                      </div>
                    </div>
                  )}

                  {/* Info Alert */}
                  <div className="m-6 bg-blue-50 border border-blue-100 rounded-xl p-4 flex gap-3">
                    <div className="p-1 h-fit bg-blue-100 rounded-lg">
                      <Bell className="h-4 w-4 text-blue-600" />
                    </div>
                    <div className="text-sm">
                      <p className="font-semibold text-blue-900">About Department Notifications</p>
                      <p className="text-blue-700/80 mt-0.5">
                        Employees will receive email notifications for absence reports and approved leave requests from anyone in the selected departments. If an employee's primary department changes, it will automatically be added to their notification list.
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="report-absentee">
              <Card className="border-gray-200 shadow-sm overflow-hidden text-sm lg:text-base">
                <CardHeader className="bg-white border-b border-gray-100">
                  <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                    <div>
                      <CardTitle className="text-lg font-semibold flex items-center gap-2">
                        <Bell className="h-5 w-5 text-gray-400" />
                        Absentee Report Notifications
                      </CardTitle>
                      <CardDescription>
                        Configure users who should be notified whenever an absentee report is submitted.
                      </CardDescription>
                    </div>
                    <div className="relative w-full md:w-80">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                      <input
                        type="text"
                        placeholder="Search managers..."
                        value={approverSearch}
                        onChange={(e) => {
                          setApproverSearch(e.target.value);
                          setApproverPage(1);
                        }}
                        className="w-full pl-10 pr-4 py-2 bg-gray-50 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm transition-all"
                      />
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="p-0">
                  <div className="hidden md:block">
                    <Table>
                      <TableHeader className="bg-gray-50/50">
                        <TableRow>
                          <TableHead className="font-semibold text-gray-600 px-6 py-4">Name</TableHead>
                          <TableHead className="font-semibold text-gray-600 px-6 py-4">Department</TableHead>
                          <TableHead className="font-semibold text-gray-600 px-6 py-4">User Role</TableHead>
                          <TableHead className="font-semibold text-gray-600 px-6 py-4 text-center">Receive Absentee Reports</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {loadingApprovers ? (
                          <TableRow>
                            <TableCell colSpan={4} className="text-center py-20 text-gray-500">
                              <div className="flex flex-col items-center gap-2">
                                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                                <span>Loading approvers...</span>
                              </div>
                            </TableCell>
                          </TableRow>
                        ) : approvers.length === 0 ? (
                          <TableRow>
                            <TableCell colSpan={4} className="text-center py-20 text-gray-500">
                              <div className="flex flex-col items-center gap-2">
                                <Filter className="h-10 w-10 text-gray-200" />
                                <p className="font-medium">No managers found</p>
                              </div>
                            </TableCell>
                          </TableRow>
                        ) : (
                          approvers.map((user) => (
                            <TableRow key={user.id} className="hover:bg-gray-50/50 transition-colors">
                              <TableCell className="px-6 py-4 font-medium text-gray-900 text-sm whitespace-nowrap">{user.name}</TableCell>
                              <TableCell className="px-6 py-4 text-gray-600 text-sm whitespace-nowrap">{user.department}</TableCell>
                              <TableCell className="px-6 py-4">
                                <Badge variant="outline" className="font-normal border-gray-200 text-xs text-gray-500">
                                  {user.userRole}
                                </Badge>
                              </TableCell>
                              <TableCell className="px-6 py-4">
                                <div className="flex items-center justify-center gap-3">
                                  <span className={`text-xs font-medium ${user.isAbsenteeReportCCd ? 'text-blue-600' : 'text-gray-400'}`}>
                                    {user.isAbsenteeReportCCd ? 'Subscribed' : 'Off'}
                                  </span>
                                  <Switch
                                    checked={user.isAbsenteeReportCCd}
                                    onCheckedChange={() => handleToggleAbsenceReport(user.id)}
                                  />
                                </div>
                              </TableCell>
                            </TableRow>
                          ))
                        )}
                      </TableBody>
                    </Table>
                  </div>

                  {/* Mobile View for Absentee Report */}
                  <div className="block md:hidden divide-y divide-gray-100">
                    {loadingApprovers ? (
                      <div className="text-center py-20">Loading...</div>
                    ) : approvers.map((user) => (
                      <div key={user.id} className="p-4 flex items-center justify-between bg-white hover:bg-gray-50/50 transition-colors">
                        <div>
                          <h3 className="font-bold text-gray-900 text-sm">{user.name}</h3>
                          <p className="text-xs text-gray-500">{user.userRole} • {user.department}</p>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className={`text-[10px] font-bold uppercase tracking-wider ${user.isAbsenteeReportCCd ? 'text-blue-600' : 'text-gray-400'}`}>
                            {user.isAbsenteeReportCCd ? 'Subscribed' : 'Off'}
                          </span>
                          <Switch
                            checked={user.isAbsenteeReportCCd}
                            onCheckedChange={() => handleToggleAbsenceReport(user.id)}
                          />
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Pagination */}
                  {totalApproverPages > 1 && (
                    <div className="px-4 py-4 md:px-6 border-t border-gray-100 flex flex-col md:flex-row items-center justify-between gap-4 bg-white">
                      <p className="text-sm text-gray-500">
                        Showing <span className="font-medium">{((approverPage - 1) * 15) + 1}</span> to <span className="font-medium">{Math.min(approverPage * 15, totalApproverCount)}</span> of <span className="font-medium">{totalApproverCount}</span> users
                      </p>
                      <div className="flex items-center gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          disabled={approverPage === 1 || loadingApprovers}
                          onClick={() => setApproverPage(prev => prev - 1)}
                        >
                          <ChevronLeft className="h-4 w-4 mr-1" />
                          Previous
                        </Button>
                        <div className="flex items-center gap-1 font-medium text-sm px-2 bg-gray-50 rounded-lg h-9">
                          <span className="text-blue-600 font-bold">{approverPage}</span>
                          <span className="text-gray-300">/</span>
                          <span>{totalApproverPages}</span>
                        </div>
                        <Button
                          variant="outline"
                          size="sm"
                          disabled={approverPage === totalApproverPages || loadingApprovers}
                          onClick={() => setApproverPage(prev => prev + 1)}
                        >
                          Next
                          <ChevronRight className="h-4 w-4 ml-1" />
                        </Button>
                      </div>
                    </div>
                  )}

                  {/* Info Alert */}
                  <div className="m-6 bg-blue-50 border border-blue-100 rounded-xl p-4 flex gap-3">
                    <div className="p-1 h-fit bg-blue-100 rounded-lg">
                      <Mail className="h-4 w-4 text-blue-600" />
                    </div>
                    <div className="text-sm">
                      <p className="font-semibold text-blue-900">About Absentee Report Notifications</p>
                      <p className="text-blue-700/80 mt-0.5">
                        Users toggled "On" will receive an email whenever a manager reports a staff member as absent.
                        This helps managers stay informed about who is away on any given day.
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </TabsContent>

        <TabsContent value="holidays">
          <Card className="border-gray-200 shadow-sm overflow-hidden text-sm lg:text-base">
            <CardHeader className="bg-white border-b border-gray-100">
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-lg font-semibold flex items-center gap-2">
                    <CalendarDays className="h-5 w-5 text-gray-400" />
                    Public Holidays
                  </CardTitle>
                  <CardDescription>
                    Configure holidays that will be shown on the calendar and optionally excluded from leave deductions.
                  </CardDescription>
                </div>
                <Dialog open={isHolidayDialogOpen} onOpenChange={setIsHolidayDialogOpen}>
                  <DialogTrigger asChild>
                    <Button className="rounded-full bg-blue-600 hover:bg-blue-700 text-white flex items-center gap-2 shadow-sm">
                      <Plus className="h-4 w-4" />
                      Add Holiday
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="sm:max-w-[425px] rounded-[32px] border-none shadow-2xl">
                    <DialogHeader>
                      <DialogTitle className="text-2xl font-black text-gray-900">Add Public Holiday</DialogTitle>
                      <DialogDescription className="text-gray-500 font-medium mt-1">
                        New holiday will be visible on the leave calendar for all staff.
                      </DialogDescription>
                    </DialogHeader>
                    <div className="grid gap-6 py-6">
                      <div className="space-y-2">
                        <Label htmlFor="name" className="text-xs font-black uppercase tracking-widest text-gray-400 ml-1">Holiday Name</Label>
                        <Input
                          id="name"
                          placeholder="e.g. Good Friday"
                          className="rounded-2xl bg-gray-50 border-gray-100 focus:ring-blue-500 h-12"
                          value={newHoliday.name}
                          onChange={(e) => setNewHoliday({ ...newHoliday, name: e.target.value })}
                        />
                      </div>
                      <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-2">
                          <Label htmlFor="startDate" className="text-xs font-black uppercase tracking-widest text-gray-400 ml-1">Start Date</Label>
                          <Input
                            id="startDate"
                            type="date"
                            className="rounded-2xl bg-gray-50 border-gray-100 focus:ring-blue-500 h-12"
                            value={newHoliday.startDate}
                            onChange={(e) => setNewHoliday({ ...newHoliday, startDate: e.target.value })}
                          />
                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="endDate" className="text-xs font-black uppercase tracking-widest text-gray-400 ml-1">End Date (Optional)</Label>
                          <Input
                            id="endDate"
                            type="date"
                            className="rounded-2xl bg-gray-50 border-gray-100 focus:ring-blue-500 h-12"
                            value={newHoliday.endDate}
                            onChange={(e) => setNewHoliday({ ...newHoliday, endDate: e.target.value })}
                          />
                        </div>
                      </div>
                      <div className="flex items-center justify-between bg-blue-50/50 p-4 rounded-2xl border border-blue-100/50 group transition-all">
                        <div className="space-y-0.5">
                          <Label className="text-sm font-bold text-blue-900 flex items-center gap-2">
                            Skip Leave Deduction
                          </Label>
                          <p className="text-xs text-blue-600/70 font-medium">Do not deduct this from balances</p>
                        </div>
                        <Switch
                          checked={newHoliday.doNotDeduct}
                          onCheckedChange={(checked) => setNewHoliday({ ...newHoliday, doNotDeduct: checked })}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="recurYears" className="text-xs font-black uppercase tracking-widest text-gray-400 ml-1">Recur for (years)</Label>
                        <Input
                          id="recurYears"
                          type="number"
                          min="1"
                          max="10"
                          className="rounded-2xl bg-gray-50 border-gray-100 focus:ring-blue-500 h-12"
                          value={newHoliday.recurYears}
                          onChange={(e) => setNewHoliday({ ...newHoliday, recurYears: parseInt(e.target.value) || 1 })}
                        />
                        <p className="text-[10px] text-gray-400 ml-1">Specify how many years this holiday repeats on the same date.</p>
                      </div>
                    </div>
                    <DialogFooter>
                      <Button onClick={handleAddHoliday} className="w-full rounded-2xl h-12 bg-blue-600 hover:bg-blue-700 text-white font-bold shadow-lg shadow-blue-200">
                        Create Holiday
                      </Button>
                    </DialogFooter>
                  </DialogContent>
                </Dialog>
              </div>
            </CardHeader>
            <CardContent className="p-0">
              <Table>
                <TableHeader className="bg-gray-50/50">
                  <TableRow>
                    <TableHead className="font-semibold text-gray-600 px-6 py-4">Holiday Name</TableHead>
                    <TableHead className="font-semibold text-gray-600 px-6 py-4">Period</TableHead>
                    <TableHead className="font-semibold text-gray-600 px-6 py-4">Total Days</TableHead>
                    <TableHead className="font-semibold text-gray-600 px-6 py-4 text-center">Skip Deduction</TableHead>
                    <TableHead className="font-semibold text-gray-600 px-6 py-4 text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {loadingHolidays ? (
                    <TableRow>
                      <TableCell colSpan={5} className="text-center py-20 text-gray-500">
                        <div className="flex flex-col items-center gap-2">
                          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                          <span>Loading holidays...</span>
                        </div>
                      </TableCell>
                    </TableRow>
                  ) : holidays.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={5} className="text-center py-20 text-gray-500">
                        <div className="flex flex-col items-center gap-2 px-6">
                          <CalendarDays className="h-10 w-10 text-gray-200" />
                          <p className="font-medium">No holidays configured</p>
                          <p className="text-sm">Click "Add Holiday" to get started</p>
                        </div>
                      </TableCell>
                    </TableRow>
                  ) : (
                    holidays.map((holiday) => {
                      const start = new Date(holiday.startDate);
                      const end = new Date(holiday.endDate || holiday.startDate);
                      const diffDays = Math.ceil(Math.abs(end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24)) + 1;

                      return (
                        <TableRow key={holiday.id} className="hover:bg-gray-50/50 transition-colors">
                          <TableCell className="px-6 py-4 font-medium text-gray-900 text-sm">{holiday.name}</TableCell>
                          <TableCell className="px-6 py-4 text-gray-600 text-sm">
                            {format(start, "MMM d, yyyy")}
                            {holiday.endDate && holiday.endDate !== holiday.startDate && (
                              <span className="text-gray-400 mx-1">→</span>
                            )}
                            {holiday.endDate && holiday.endDate !== holiday.startDate && format(end, "MMM d, yyyy")}
                          </TableCell>
                          <TableCell className="px-6 py-4">
                            <Badge variant="secondary" className="bg-gray-100 text-gray-700 font-medium">
                              {diffDays} day{diffDays > 1 ? 's' : ''}
                            </Badge>
                          </TableCell>
                          <TableCell className="px-6 py-4">
                            <div className="flex justify-center">
                              {holiday.doNotDeduct ? (
                                <Badge className="bg-green-50 text-green-700 border-green-100 hover:bg-green-50">Enabled</Badge>
                              ) : (
                                <Badge variant="outline" className="text-gray-400 border-gray-100">Disabled</Badge>
                              )}
                            </div>
                          </TableCell>
                          <TableCell className="px-6 py-4 text-right">
                            <Button
                              variant="ghost"
                              size="icon"
                              onClick={() => handleDeleteHoliday(holiday.id)}
                              className="text-gray-400 hover:text-red-600 hover:bg-red-50"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>

              {/* Holiday Info Alert */}
              <div className="m-6 bg-gray-50 border border-gray-100 rounded-2xl p-4 flex gap-3">
                <div className="p-1 h-fit bg-white rounded-lg shadow-sm border border-gray-100">
                  <Info className="h-4 w-4 text-blue-600" />
                </div>
                <div className="text-sm">
                  <p className="font-semibold text-gray-900">How holidays work</p>
                  <p className="text-gray-500 mt-0.5">
                    Holidays with "Skip Deduction" enabled will not be counted as leave hours if they overlap with an employee's requested leave period. Weekends are automatically excluded from deductions by default.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default AttendanceSettings;
